import { FaCalendarCheck } from "react-icons/fa";
import type { TFunction } from "i18next";
import type { DashboardUpcomingItem } from "../types/dashboard.types";
import { formatUpcomingMeta } from "../utils/dashboardDate.utils";
import DashboardUpcomingDetails from "./DashboardUpcomingDetails";

interface DashboardUpcomingPanelProps {
  items: DashboardUpcomingItem[];
  loading: boolean;
  selected: DashboardUpcomingItem | null;
  locale: string;
  agendaActivitiesLabel: string;
  t: TFunction;
  onSelect: (item: DashboardUpcomingItem | null) => void;
  onNavigate: (path: string) => void;
}

const DashboardUpcomingPanel = ({ items, loading, selected, locale, agendaActivitiesLabel, t, onSelect, onNavigate }: DashboardUpcomingPanelProps) => {
  const visibleItems = items.slice(0, 3);
  const hiddenCount = Math.max(items.length - visibleItems.length, 0);

  return (
    <aside className="w-full self-start rounded-2xl border border-slate-200/80 bg-white/90 shadow-[0_12px_30px_rgba(15,23,42,0.07)] backdrop-blur dark:border-white/10 dark:bg-slate-950/50">
      <div className="px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-3">
          {selected ? (
            <span className="text-base font-bold text-slate-950 dark:text-white">
              {t("dashboard.personal.upcomingDetails", { defaultValue: "Upcoming details" })}
            </span>
          ) : (
            <h3 className="text-base font-bold text-slate-950 dark:text-white">{t("dashboard.personal.upcoming", { defaultValue: "Upcoming" })}</h3>
          )}
          {loading && !selected ? (
            <span className="text-xs text-slate-500 dark:text-slate-300">{t("common.loading", { defaultValue: "Loading..." })}</span>
          ) : null}
        </div>
      </div>

      {selected ? (
        <DashboardUpcomingDetails item={selected} locale={locale} t={t} onBack={() => onSelect(null)} onOpen={() => onNavigate(selected.path)} />
      ) : (
        <div className="overflow-hidden border-y border-slate-200/80 dark:border-white/10">
          {loading && items.length === 0 ? (
            <div className="space-y-3 px-4 py-4">
              {[0, 1, 2].map((index) => (
                <div key={index} className="h-10 rounded-xl bg-slate-100 motion-safe:animate-pulse dark:bg-white/10" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
              {t("dashboard.personal.empty", { defaultValue: "No upcoming items found." })}
            </p>
          ) : (
            visibleItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item)}
                className={`group flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-indigo-50/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:hover:bg-indigo-950/30 ${
                  index > 0 ? "border-t border-slate-200/80 dark:border-white/10" : ""
                }`}
              >
                <span
                  className={`h-3 w-3 flex-shrink-0 rounded-full ${
                    item.source === "calendar" && item.type !== agendaActivitiesLabel
                      ? "bg-slate-400 shadow-[0_0_0_4px_rgba(148,163,184,0.14)]"
                      : "bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.14)]"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-slate-950 dark:text-white">{item.title}</p>
                  <p className="mt-1 flex min-w-0 items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                    <FaCalendarCheck className="shrink-0 text-[12px] text-slate-400" />
                    <span className="truncate">{formatUpcomingMeta(item, locale, t("dashboard.all_day", { defaultValue: "All day" }))}</span>
                  </p>
                </div>
                <span className="text-xl leading-none text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-indigo-500 motion-reduce:transform-none" aria-hidden="true">
                  ›
                </span>
              </button>
            ))
          )}

          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => onNavigate("/my/outings")}
              className="flex w-full items-center justify-between border-t border-slate-200/80 px-4 py-3 text-left text-sm font-bold text-indigo-700 transition hover:bg-indigo-50/70 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:border-white/10 dark:text-indigo-200 dark:hover:bg-indigo-950/30"
            >
              <span>{t("dashboard.personal.fullAgenda", { defaultValue: "Go to full agenda" })}</span>
              <span className="text-xl leading-none" aria-hidden="true">›</span>
            </button>
          ) : null}
        </div>
      )}
    </aside>
  );
};

export default DashboardUpcomingPanel;
